import type { Conversation, TurnOptions } from "@esi/shared";
import { useRef, useState } from "react";
import { blockedBySessionEnd } from "../../api.ts";
import { type AttachmentDraft, attachmentDraft, attachmentError } from "./controller.ts";
import { attachmentSessionGeneration } from "./session.ts";

/** One creation in flight, even when drop/picker selections arrive before its response. */
export function useAttachmentStart(props: {
  source: AttachmentDraft;
  csrfToken: string;
  create: () => Promise<Conversation>;
  started: (conversation: Conversation) => void;
  options: () => TurnOptions | undefined;
}) {
  const flight = useRef<Promise<void> | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string>();
  const select = (files: File[]) => {
    if (blockedBySessionEnd()) {
      return;
    }
    if (files.length === 0 && props.source.snapshot().files.length === 0) {
      return;
    }
    if (files.length > 0) {
      props.source.add(files);
    }
    props.source.edit({ options: props.options() });
    if (flight.current) {
      return;
    }
    const generation = attachmentSessionGeneration();
    setStarting(true);
    setError(undefined);
    flight.current = props
      .create()
      .then((created) => {
        if (generation !== attachmentSessionGeneration()) {
          return;
        }
        const state = props.source.snapshot();
        const target = attachmentDraft(created.id, props.csrfToken);
        target.edit({ text: state.text, options: state.options });
        target.add(state.files.flatMap((f) => (f.file ? [f.file] : [])));
        props.source.sent();
        props.source.edit({ review: false });
        props.started(created);
      })
      .catch((error) => {
        // Source still owns the files, effort and text. Another selection retries creation.
        setError(attachmentError(error));
      })
      .finally(() => {
        flight.current = null;
        setStarting(false);
      });
  };
  return { select, starting, error };
}
